/**
 * OffshoreProtectedRoute — OS-INV-04
 *
 * Guards /os/* protected routes. Mirrors MadebbProtectedRoute but:
 *   - Redirects UNAUTHENTICATED users to the OS landing ('/os'), not a blank screen.
 *   - Shows an "Access Denied" panel for AUTHENTICATED users WITHOUT the 'offshore'
 *     tenant claim (read from AuthContext.tenants, which comes from the Firebase
 *     ID token custom claims via authService.getTenantClaims()).
 *   - A user whose tenants include 'offshore' sees the dashboard. The superadmin
 *     override email (skipsavoie@gmail.com) is allowed through regardless of the
 *     tenant claim so Skip can always reach OS, matching the staff override pattern.
 *
 * It also wraps children in a TenantProvider tenant="offshore" so that every
 * descendant data call (via useTenant().apiBase) targets /api/os/*.
 *
 * Honors the same VITE_E2E_TEST_MODE bypass as MadebbProtectedRoute.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { TenantProvider } from '../../contexts/TenantContext';

/** Override emails that may always access OS (mirrors STAFF_OVERRIDE_EMAILS). */
const OS_OVERRIDE_EMAILS = ['skipsavoie@gmail.com', 'trim@findsanctuary.ai'];

/** True when the user is entitled to Offshore Sails. */
export function hasOffshoreAccess(
  email: string | null | undefined,
  tenants: string[]
): boolean {
  if (email && OS_OVERRIDE_EMAILS.includes(email.toLowerCase())) return true;
  return tenants.includes('offshore');
}

interface OffshoreProtectedRouteProps {
  children: React.ReactNode;
}

export const OffshoreProtectedRoute: React.FC<OffshoreProtectedRouteProps> = ({ children }) => {
  const { user, loading, tenants } = useAuth();

  // LOCAL E2E BYPASS — mirrors MadebbProtectedRoute. Never set in production.
  if (import.meta.env.VITE_E2E_TEST_MODE === 'true') {
    return <TenantProvider tenant="offshore">{children}</TenantProvider>;
  }

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        gap={2}
      >
        <CircularProgress size={50} />
        <Typography variant="h6" color="text.secondary">
          Loading…
        </Typography>
      </Box>
    );
  }

  // Unauthenticated → back to OS landing (not a blank screen).
  if (!user) {
    return <Navigate to="/os" replace />;
  }

  // Authenticated but lacks the 'offshore' tenant claim → Access Denied.
  if (!hasOffshoreAccess(user.email, tenants)) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          background: 'linear-gradient(135deg, #002680 0%, #001550 50%, #000a2e 100%)',
        }}
      >
        <Paper sx={{ p: 4, maxWidth: 460, textAlign: 'center' }} data-testid="os-access-denied">
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You don&apos;t have access to Offshore Sails. If you believe this is a
            mistake, contact your administrator to be granted the Offshore Sails tenant.
          </Typography>
          <Button
            variant="contained"
            href="/os"
            sx={{ background: '#FFC726', color: '#001550', '&:hover': { background: '#ffd454' } }}
          >
            Back to Landing
          </Button>
        </Paper>
      </Box>
    );
  }

  // Authorized — provide the offshore tenant so all data calls hit /api/os/*.
  return <TenantProvider tenant="offshore">{children}</TenantProvider>;
};
